import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import gql from 'graphql-tag';

import { CancelPaymentStatus, CancelPaymentMutationVariables } from '../gql-types.generated';

import { getStandardHeaders } from '../util/StandardRequestHeaders';

// Voids a card payment that has not yet settled at the processor. Unlike createRefund this carries no
// idempotency key - the mutation does not accept one. A repeat call is largely self-limiting because the
// service refuses a payment that is no longer `completed`, but it is not formally guaranteed.
// eslint-disable-next-line @typescript-eslint/naming-convention
const MUTATION_CANCEL_PAYMENT = gql`
  mutation cancelPayment($input: CancelPaymentInput!) {
    cancelPayment(input: $input) {
      code
      error
      message
      errorReason {
        code
        message
      }
      payment {
        id
        status
        settlementStatus
      }
    }
  }
`;

export const mutationCancelPayment = async (
  client: ApolloClient<NormalizedCacheObject>,
  paymentId: string,
  reason: string,
): Promise<CancelPaymentStatus | undefined> => {
  // NOT getStandardHeaders(true) - that argument is excludeTenantId, and dropping x-aptean-tenant
  // makes the backend resolve the caller as LIMITED_USER (GraphQlServer.ts:199), whose permission
  // set is LoginContextRead + TenantAccountRead only. cancelPayment then fails its @Authorized
  // check with "Access denied" regardless of the user's real role. Only the cross-tenant Aptean
  // admin mutations legitimately pass true.
  const headers = await getStandardHeaders();
  const { data, errors } = await client.mutate<{ cancelPayment: CancelPaymentStatus }, CancelPaymentMutationVariables>({
    variables: { input: { paymentId, reason } },
    mutation: MUTATION_CANCEL_PAYMENT,
    context: { headers },
  });

  if (errors && errors.length > 0) {
    errors.forEach(error => console.error(error));
    throw new Error('An error has occurred while voiding the payment.');
  }

  return data?.cancelPayment;
};
